'use client';

import { useCallback, useMemo } from 'react';
import { Box } from '@terra/ui-styled-system';
import {
  ArtifactsProvider,
  BodyDefault,
  ChatHarProvider,
  TraceFilesProvider,
  TraceView,
} from '@terra/ui-design-system';
import type { SmokeTestHistoryResponseDto } from '@terra/shared-contracts-smoke-test-history';
import { useAppTargetId } from '../../store/projectStore';
import { useSmokeTestHistory } from '../../hooks/api/useSmokeTestHistory';
import { useTracesSSE } from '../../hooks/useTracesSSE';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useHarById } from '../../hooks/api/useHarById';
import { useHarDisplayConfig } from '../../hooks/useHarDisplayConfig';
import { useToolHarPresence } from '../../hooks/useToolHarPresence';
import { useTranslations } from 'next-intl';

type ExecutionStatus = 'running' | 'completed' | 'error' | 'aborted';

function executionStatus(
  history: SmokeTestHistoryResponseDto | undefined,
): ExecutionStatus {
  if (history?.metadata?.skipAttackReason) {
    return 'aborted';
  }

  switch (history?.status) {
    case 'RUNNING':
      return 'running';
    case 'COMPLETED':
      return 'completed';
    case 'ABORTED':
    case 'PAUSED':
      return 'aborted';
    case 'FAILED':
      return 'error';
    default:
      return history?.plan?.some((item) => item.status === 'in_progress')
        ? 'running'
        : 'completed';
  }
}

export function NeoExecutionSteps({ threadId }: { threadId: string }) {
  const t = useTranslations('chat.copilot');
  const applicationId = useAppTargetId();
  const {
    data: history,
    isLoading,
    error,
    refetch,
  } = useSmokeTestHistory({
    threadId,
    enabled: threadId.length > 0,
  });

  useTracesSSE({
    applicationId: applicationId ?? '',
    threadId,
    enabled: threadId.length > 0 && Boolean(applicationId),
    onTraceEvent: () => {
      void refetch();
    },
  });

  const fetchHarData = useCallback(
    async ({
      threadId,
      toolCallId,
    }: {
      threadId: string;
      toolCallId: string;
    }) => {
      try {
        const response = await terraProxyApiClient.getToolHarsByToolCallId({
          threadId,
          toolCallId,
        });

        return response?.hars ?? [];
      } catch (fetchError: unknown) {
        console.error(
          'Failed to fetch HAR data:',
          fetchError instanceof Error
            ? fetchError
            : new Error(String(fetchError)),
        );

        return [];
      }
    },
    [],
  );

  const { fetchHarById } = useHarById();
  const { hasHarPresence } = useToolHarPresence(threadId);
  const harDisplay = useHarDisplayConfig(applicationId);
  const traceFiles = useMemo(
    () =>
      history?.files?.filter(
        (file): file is NonNullable<typeof file> => file !== null,
      ) ?? [],
    [history?.files],
  );

  if (isLoading && !history) {
    return <BodyDefault color="fg.muted">{t('neoStepsLoading')}</BodyDefault>;
  }

  if (error || !history) {
    return <BodyDefault color="fg.error">{t('neoStepsError')}</BodyDefault>;
  }

  return (
    <ChatHarProvider
      threadId={threadId}
      fetchHarData={fetchHarData}
      fetchHarById={fetchHarById}
      hasHarPresence={hasHarPresence}
      harDisplay={harDisplay}
    >
      <TraceFilesProvider traceFiles={traceFiles}>
        <ArtifactsProvider artifacts={history.artifacts ?? []}>
          <Box flex="1" h="full" minH={0} overflow="hidden">
            <TraceView
              history={history}
              maxHeight="100%"
              status={executionStatus(history)}
              showHeader={false}
              showToolbar={false}
            />
          </Box>
        </ArtifactsProvider>
      </TraceFilesProvider>
    </ChatHarProvider>
  );
}
